import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isEqual } from 'lodash';
import { useFieldArray, useFormContext } from 'react-hook-form';
import {
	UNIQUE_ERROR_TYPE,
	HIERARCHY_LABEL_GROUP_VALUES,
	getAllowedChildLabels,
	isLeafLabel
} from 'app-config';
import {
	Icon,
	Input,
	Select,
	Button,
	Tooltip,
	Fx,
	Space,
	Notification
} from 'Components';
import { useConfigs } from 'Hooks';
import Level, { LevelProps } from '../../../components/Level/Level';
import styles from './LevelItem.module.scss';
import UploadGroupsButton from '../UploadGroupsButton/UploadGroupsButton';

import { useGroupsHierarchyContext } from '../../../GroupsHierarchyContext';
import {
	isGroupsHierarchyLimitReached,
	validateGroupName
} from '../../../GroupsHierarchy.helpers';
import getIndexes from 'Helpers/getIndexes';
import { getHierarchyLabel } from 'Services/Api/HierarchyLabel/HierarchyLabelService';

interface LevelItemHeaderProps {
	onChevronClick: () => void;
	isFolded: boolean;
	levelNumber: number;
	disabled: boolean;
	onChange: (value: boolean, levelNumber: number) => void;
}

const LevelItemHeader = ({
	onChevronClick,
	isFolded,
	levelNumber,
	disabled,
	onChange
}: LevelItemHeaderProps) => {
	const { t } = useTranslation();
	const { parent, parentLevel } = useGroupsHierarchyContext();
	const { watch } = useFormContext();

	// For new tenants (is_new=true): no default value (user must select)
	// For old tenants (is_new=false) with isProperty=true: default to "Property"
	// For old tenants (is_new=false) with isProperty=false: no default value
	const hierarchyLabelDefault =
		parent && !parent.is_new && parent.isProperty ? 'Property' : undefined;

	// Fetch the parent group's hierarchy label from the backend so the first
	// level's dropdown can be filtered based on the parent's actual label.
	const [parentHierarchyLabel, setParentHierarchyLabel] = useState<
		string | undefined
	>();
	const [parentLabelLoading, setParentLabelLoading] = useState<boolean>(!!parent?.id);
	useEffect(() => {
		if (parent?.id) {
			setParentLabelLoading(true);
			getHierarchyLabel(parent.id)
				.then((res) => {
					if (res.success && res.data) {
						setParentHierarchyLabel(res.data.node_type);
					}
				})
				.catch(() => {
					// Silently ignore – dropdown will show all options
				})
				.finally(() => {
					setParentLabelLoading(false);
				});
		}
	}, [parent?.id]);

	// Watch the previous level's hierarchy label to filter allowed child labels.
	// If this is the first child level (parentLevel + 1), use the actual parent
	// group's hierarchy label fetched from the backend.
	const prevLevelNumber = levelNumber - 1;
	const prevLevelHierarchyLabel: string | undefined =
		prevLevelNumber > parentLevel
			? watch(`level${prevLevelNumber}.hierarchyLabel`)
			: parentHierarchyLabel;

	const allowedLabels = useMemo(
		() => getAllowedChildLabels(prevLevelHierarchyLabel),
		[prevLevelHierarchyLabel]
	);

	const isPrevLevelLeaf = isLeafLabel(prevLevelHierarchyLabel);

	return (
		<Fx justify="space-between" grow={1}>
			<Fx align="middle">
				<span className={styles.property}>{t('Type')}</span>
				<Select.HookFormSelect
					name={`level${levelNumber}.hierarchyLabel`}
					defaultValue={hierarchyLabelDefault}
					className={styles.radioGroup}
					disabled={parentLabelLoading || isPrevLevelLeaf}
				>
					{allowedLabels.map((label) => (
						<Select.Option key={label} value={label}>
							{label}
						</Select.Option>
					))}
				</Select.HookFormSelect>
			</Fx>
			<div className={styles.headerRootRight}>
				<Space align="center">
					<Tooltip
						overlay={t('groupsHierarchy.formHierarchy.tooltip')}
					>
						<Icon type="information" />
					</Tooltip>
					<UploadGroupsButton level={levelNumber} />
					<Icon
						type="chevronDown"
						onClick={onChevronClick}
						className={isFolded ? undefined : styles.revertIcon}
					/>
				</Space>
			</div>
		</Fx>
	);
};
interface LevelItemGroupsProps {
	levelNumber: number;
}

const LevelItemGroups = ({ levelNumber }: LevelItemGroupsProps) => {
	const { t } = useTranslation();
	const {
		groupsHierarchySizeLimit,
		amountOfGroupsInTenant
	} = useGroupsHierarchyContext();
	const prevIndexes = useRef<number[]>([]);
	const {
		control,
		formState: { errors },
		setValue,
		getValues,
		setError,
		clearErrors,
		watch
	} = useFormContext();
	const { maxGroupNameCharacters } = useConfigs();
	const { fields, append } = useFieldArray({
		control,
		name: `level${levelNumber}.groupsNames`
	});

	useEffect(() => {
		if (!fields.length) {
			append({
				name: ''
			});
		}

		//eslint-disable-next-line
	}, [fields.length]);

	// Watch the current level's hierarchy label to disable adding children
	// when the level is labelled "Individual" (leaf node).
	const currentLevelHierarchyLabel = watch(`level${levelNumber}.hierarchyLabel`);
	const isCurrentLevelLeaf = isLeafLabel(currentLevelHierarchyLabel);

	const addGroupClick = () => {
		if (isCurrentLevelLeaf) {
			Notification.error({
				description: t(
					'groups.groupDetails.groupDetailsActions.tooltipLeafNode',
					{ defaultValue: '"Individual" groups cannot have child groups.' }
				)
			});
			return;
		}

		const values = getValues();

		if (
			isGroupsHierarchyLimitReached(
				values,
				groupsHierarchySizeLimit,
				amountOfGroupsInTenant,
				`level${levelNumber}`,
				1
			)
		) {
			Notification.error({
				description: t('groupsHierarchy.groupsLimit', {
					groupsHierarchySizeLimit
				})
			});

			return;
		}
		append({
			name: ''
		});
	};

	const onDeleteGroupClick = (fieldId: string, index: number) => {
		setValue(
			`level${levelNumber}.groupsNames`,
			fields.filter(({ id }) => id !== fieldId),
			{ shouldValidate: true }
		);

		if (
			errors?.[`level${levelNumber}`]?.groupsNames[index]?.name?.type ===
			UNIQUE_ERROR_TYPE
		) {
			validateGroupUniqName();
		} else {
			clearErrors(`level${levelNumber}.groupsNames[${index}]`);
		}
	};

	const validateGroupUniqName = useCallback(() => {
		const groups = getValues()[`level${levelNumber}`].groupsNames;
		if (groups.length > 1) {
			const indexes = getIndexes(groups, 'name');

			if (indexes.length) {
				if (!isEqual(indexes, prevIndexes.current)) {
					Notification.error({
						description: t(
							'groups.addMultipleGroups.errorMessageUniqueName'
						)
					});
				}

				prevIndexes.current = indexes;
				indexes.forEach((index: number) => {
					setError(`level${levelNumber}.groupsNames.${index}.name`, {
						type: UNIQUE_ERROR_TYPE
					});
				});

				return;
			}
		}
		prevIndexes.current = [];
		clearErrors(`level${levelNumber}.groupsNames`);

		// eslint-disable-next-line
	}, []);

	const handleValidateGroupName = (value: string): boolean => {
		return value.length
			? validateGroupName(maxGroupNameCharacters)(value)
			: true;
	};

	return (
		<div className={styles.itemRootContainer}>
			{fields.map(({ id }, index) => {
				return (
					<div className={styles.group} key={id}>
						<Input.HookFormInput
							label={t('group.groupName')}
							className={styles.formItem}
							name={`level${levelNumber}.groupsNames.${index}.name`}
							onBlur={validateGroupUniqName}
							validate={handleValidateGroupName}
						/>
						<Button
							shape="round"
							type="primary"
							size="small"
							ghost
							icon={<Icon type="close" />}
							disabled={fields.length === 1}
							onClick={() => onDeleteGroupClick(id, index)}
							className={styles.button}
						/>
					</div>
				);
			})}

			<div className={styles.controlContainer}>
				<Button
					type="primary"
					shape="round"
					icon={<Icon type="plus" />}
					onClick={addGroupClick}
					disabled={isCurrentLevelLeaf}
				>
					{t('groups.groupDetails.groupDetailsActions.addGroup')}
				</Button>
				<span>
					{t('groupsHierarchy.formHierarchy.totalGroups', {
						groupsAmount: fields.length
					})}
				</span>
			</div>
		</div>
	);
};

interface LevelItemProps extends LevelProps {
	disabled: boolean;
	onChange: (value: boolean, levelNumber: number) => void;
}

const LevelItem = ({ levelNumber, disabled, onChange }: LevelItemProps) => {
	const [isFolded, setIsFolded] = useState<boolean>(true);

	const toggleFolded = useCallback(() => {
		setIsFolded((isFolded) => !isFolded);
	}, []);

	return (
		<Level
			levelNumber={levelNumber}
			Component={
				<LevelItemHeader
					onChevronClick={toggleFolded}
					isFolded={isFolded}
					levelNumber={levelNumber}
					disabled={disabled}
					onChange={onChange}
				/>
			}
		>
			{isFolded ? <LevelItemGroups levelNumber={levelNumber} /> : null}
		</Level>
	);
};

export default LevelItem;
